"use client"
import React, { useState, useEffect } from 'react';
import { fr }  from 'date-fns/locale/fr';
import ReactDatePicker from 'react-datepicker';
import { Modal } from 'flowbite-react';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { IParamsDownloadVente } from '@/type';
import { Prisma } from '@prisma/client';
import { usePDF } from '@react-pdf/renderer';
import NextVentesPdf from '../Pdf/NextVentesPdf';

type DownloadNextVentesModalProps = 
{
    showModal: boolean
    setShowModal: (show: boolean) => void
    tgis?: Prisma.TgiGetPayload<{}>[]
    ventes: Prisma.VenteGetPayload<{}>[]
}
const DownloadNextVentesModal = ({ showModal, setShowModal, tgis, ventes }: DownloadNextVentesModalProps) => 
{
    const [searchFields, setSearchFields] = useState<IParamsDownloadVente>(
    {
        tgi: null,
        dateVente : null
    }) 

    const [availableDateForTgi, setAvailableDateForTgi] = useState<Date[]>([])
    const [ventesToDownload, setVentesToDownload] = useState<Prisma.VenteGetPayload<{}>[]>([])    
    const [selectedTgi, setSelectedTgi] = useState<Prisma.TgiGetPayload<{}>>()
    const [instance, updateInstance] = usePDF( );

    useEffect(() => 
    {
        if (ventesToDownload.length === 0) 
        {
            updateInstance(<></>)
            
        } else {
            updateInstance(<NextVentesPdf tgi={selectedTgi} ventes={ventesToDownload} />)
        }
    }, [ventesToDownload, selectedTgi, updateInstance])

    useEffect(() => {
        const availableDate = ventes.filter((vente) => vente.tgiId === searchFields.tgi).map((vente) => vente.dateVente && DateTime.fromJSDate(vente.dateVente, { zone: 'Europe/Paris' }).toJSDate())
        setAvailableDateForTgi(availableDate.filter((date): date is Date => date !== null))
    }, [searchFields.tgi, ventes])
    
    const handleCloseModal = () => 
    {   
        setSearchFields(
        {
            tgi: null,
            dateVente : null
        })
        setShowModal(false)
    }
    useEffect(() =>
    {
        if(searchFields.tgi !== null && searchFields.dateVente !== null)
        {
            const selectedVentes = ventes.filter((vente) =>
            {
                return vente.tgiId === searchFields.tgi && DateTime.fromJSDate(vente.dateVente!, { zone: 'Europe/Paris' }).toISODate() === searchFields.dateVente?.toISODate()
            })
            if(selectedVentes.length > 0)
            {
                setVentesToDownload(selectedVentes)
                setSelectedTgi(tgis?.filter((tgi) => tgi.id === searchFields.tgi)[0])
            }
        } else {
            setVentesToDownload([])
            setSelectedTgi(undefined)
        }
    }, [searchFields, tgis, ventes])
    
    const tgiGrass = tgis?.find((tgi) => tgi.ville?.toUpperCase() === 'GRASSE')
    const tgiNice = tgis?.find((tgi) => tgi.ville?.toUpperCase() === 'NICE')

    // filtre des TGIS de Grasses et Nice
    const tgisFiltered = tgis?.filter((tgi) => tgi.id !== tgiGrass?.id && tgi.id !== tgiNice?.id)

    // ajout de grasse et nice en debut de tableau
    const sortedTgis = tgisFiltered ? [tgiGrass, tgiNice, ...tgisFiltered] : []
    
    return ( 
        <Modal show={showModal} onClose={() => handleCloseModal()} size={'7xl'} position={"top-center"} className='z-[650]'>
            <Modal.Header>
                <div className="text-base md:text-xl font-bold text-center">
                    Préparez-vous aux prochaines ventes aux enchères
                </div>
            </Modal.Header>
            <Modal.Body>
                <form className='flex flex-col justify-between space-y-4'>
                    <div className="">

                        <div className="flex justify-center mb-4">
                            <div className="w-full">
                                <p className='text-center'>
                                    Téléchargez votre support pour assister aux prochaines ventes aux enchères
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col space-x-0 space-y-4 md:flex-row md:space-x-4 md:space-y-0">
                            <div className='w-full md:w-1/2 z-[100]'>
                                <Select
                                    placeholder="Tribunal Judiciaire"
                                    styles={{
                                        control: (provided: any) => ({
                                            ...provided,
                                            width: '100%',
                                        }),
                                        container: (provided: any) => ({
                                            ...provided,
                                            width: '100%',
                                        }),
                                        menu: (provided: any) => ({
                                            ...provided,
                                            width: '100%',
                                            zIndex: 9999, 
                                        }),
                                        menuPortal: (provided: any) => ({
                                            ...provided,
                                            zIndex: 9999,
                                        }),
                                    }}
                                    menuPortalTarget={document.body} // Rendre le menu dans un portail
                                    menuPosition="fixed"
                                    options={sortedTgis}
                                    getOptionLabel={(option: any) => `${option.nom} (${option.codePostal} ${option.ville})`}
                                    getOptionValue={(option: any) => option.id!.toString()}
                                    noOptionsMessage={() => "Aucun TJI correspondant"}
                                    isClearable={true}
                                    isSearchable={true}
                                    onChange={(selectedOption: any) => {
                                        setSearchFields({ ...searchFields, tgi: selectedOption?.id ?? null, dateVente: null })
                                    }}
                                />
                            </div>
                            <div className='w-100 md:w-1/2 hasAvailable'>
                                <ReactDatePicker
                                    id='dateVente'
                                    name='dateVente'
                                    className='w-full rounded-md border border-zinc-300 bg-white text-zinc-700 focus:border-zinc-500 focus:ring-zinc-500 focus:ring-1 duration-700 hover:border-zinc-500 hover:ring-zinc-500 hover:ring-1 px-2 py-1.5'
                                    disabledKeyboardNavigation
                                    dateFormat="dd/MM/yyyy"
                                    onKeyDown={(e) => { e.preventDefault() }}
                                    onChange={(date) => setSearchFields({...searchFields, dateVente: (date !== null) ? DateTime.fromJSDate(date) : null })}
                                    locale={fr}
                                    includeDates={availableDateForTgi.filter((date): date is Date => date !== null)}
                                    selected={searchFields?.dateVente?.toJSDate()}
                                    isClearable={true}
                                    autoComplete='off'
                                    placeholderText='Sélectionner une date'
                                    disabled={searchFields.tgi === null}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-row justify-end'>
                        {selectedTgi && ventesToDownload.length > 0 &&
                        (
                            instance.url ?
                                <a href={instance.url} download={`Prochaines-ventes-${selectedTgi.nom}-${DateTime.now().toISODate()}.pdf`}>
                                    <button
                                        type='button'
                                        className='bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-sm shadow-xl'
                                    >
                                        Télécharger
                                    </button>
                                </a>
                                :
                                <button
                                    type='button'
                                    className='bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-sm shadow-xl'
                                    disabled={true}
                                >
                                    Géréation du PDF
                                </button>
                        )}  
                        <button
                            type='button'
                            className='bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-sm shadow-xl ms-3'
                            onClick={(e) => handleCloseModal()}
                        >
                            Fermer
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal> 
    );
}
 
export default DownloadNextVentesModal;